/**
 * TanStack Query hooks for the feature-import feature. Same conventions as
 * `src/features/tracker/hooks.ts`: a key factory, thin useQuery/useMutation
 * wrappers over `./api`, invalidation on writes.
 */

import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/lib/queryClient';

import { getFeatureImportConfig, getLatestFeatureImport, putFeatureImportConfig, triggerFeatureImport } from './api';
import type { FeatureImportConfig, FeatureImportStatus } from './types';

export const featureImportKeys = {
  all: ['feature-import'] as const,
  config: (simulationId: string) => ['feature-import', 'config', simulationId] as const,
  latest: (simulationId: string) => ['feature-import', 'latest', simulationId] as const,
} as const;

/** The stored feature-import config, `undefined` while none is saved. */
export function useFeatureImportConfig(simulationId: string | null | undefined) {
  return useQuery({
    queryKey: simulationId ? featureImportKeys.config(simulationId) : featureImportKeys.all,
    queryFn: () => getFeatureImportConfig(simulationId!),
    enabled: !!simulationId,
  });
}

/**
 * The fields a tracker source reports as available, for the due-date field
 * picker. Enabled only once a source is selected. A live probe against the
 * tracker, so: a few minutes of staleTime (the field directory barely
 * changes within a dialog session), and a single retry — on failure the
 * dialog falls back to free-text entry, and three default retries would
 * just delay that fallback.
 */
export { useSourceFields } from '@/features/tracker/hooks';

/**
 * Upsert the config. On success the stored-config cache is updated
 * synchronously from the PUT echo (so the Import affordance appears in the
 * same tick the dialog closes), then invalidated for freshness.
 */
export function useSaveFeatureImportConfig(simulationId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (config: FeatureImportConfig) => putFeatureImportConfig(simulationId!, config),
    onSuccess: (saved) => {
      if (simulationId) {
        qc.setQueryData(featureImportKeys.config(simulationId), saved);
        void qc.invalidateQueries({ queryKey: featureImportKeys.config(simulationId) });
      }
    },
  });
}

/**
 * Trigger an import run. Invalidates the latest-run query so the first poll
 * reads the new `running` row (same idiom as `useTriggerIngest`).
 */
export function useTriggerFeatureImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (simulationId: string) => triggerFeatureImport(simulationId),
    onSuccess: (run) => {
      void qc.invalidateQueries({ queryKey: featureImportKeys.latest(run.simulation_id) });
    },
  });
}

/**
 * The newest import run for a simulation. Pass `refetchInterval` (2s while a
 * run is tracked) to poll — the DatasetsPage poll idiom.
 *
 * When a watched run transitions `running` → `ok`, the import has
 * created/updated features server-side, so the simulation query is
 * invalidated here and the features list refreshes.
 */
export function useLatestFeatureImport(
  simulationId: string | null | undefined,
  opts?: { refetchInterval?: number | false },
) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: simulationId ? featureImportKeys.latest(simulationId) : featureImportKeys.all,
    queryFn: () => getLatestFeatureImport(simulationId!),
    enabled: !!simulationId,
    refetchInterval: opts?.refetchInterval ?? false,
  });

  // Observation log: `undefined` = nothing observed yet (query still loading),
  // `null` = observed "no run", else the last observed (id, status). A run
  // "reaches ok" when an observation CHANGES into ok — a fresh run id or a
  // status flip — but not on the hook's very first observation (a page load
  // that finds an old finished run imported nothing just now).
  const run = query.data;
  const prevRef = useRef<{ id: string; status: FeatureImportStatus } | null | undefined>(undefined);
  useEffect(() => {
    if (run === undefined) return;
    const prev = prevRef.current;
    prevRef.current = run ? { id: run.id, status: run.status } : null;
    if (!run || !simulationId || prev === undefined) return;
    const changed = !prev || prev.id !== run.id || prev.status !== run.status;
    if (changed && run.status === 'ok') {
      void qc.invalidateQueries({ queryKey: queryKeys.simulation(simulationId) });
    }
  }, [run, simulationId, qc]);

  return query;
}
